import { createAsyncThunk } from "@reduxjs/toolkit";
import Decimal from "decimal.js-light";
import { DateTime } from "luxon";
import api, { noContentDecoder } from "../../api";
import { getPositionForDriverEventFromWatch } from "../../../helpers/position";
import { loadRouteWithStops } from "../../data/route-with-stops";
import { apiFetch } from "../../../helpers/apiRequest";
import { browserSessionId } from "../../api";
import { createErrorReportingAsyncThunk } from "../../helpers";
import {
  addEditNoteActivityT,
  addEditVolumeActivityT,
  addEditWeightActivityT,
  addEditInvoiceRefActivityT,
  startTripActivityT,
  endTripActivityT,
  arrivedActivityT,
  departedActivityT,
  stopCompletedActivityT,
  driverActivityCommonPropsT,
} from "dora-contracts";

const prefix = "/app/driver-activities";

// Helper to transform position to only include lat/lon (removing accuracy)
const toPosition = (
  location: { lat: number; lon: number; accuracy: number } | null
) => {
  return location ? { lat: location.lat, lon: location.lon } : null;
};

// Note endpoints
export const addNote = createErrorReportingAsyncThunk(
  `${prefix}/add-note`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      note: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, note, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/note`,
      body: addEditNoteActivityT.encode({
        note,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const editNote = createErrorReportingAsyncThunk(
  `${prefix}/edit-note`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      note: string;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, note, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).put({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/note/${id}`,
      body: addEditNoteActivityT.encode({
        note,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const deleteNote = createErrorReportingAsyncThunk(
  `${prefix}/delete-note`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    const response = await apiFetch(
      `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/note/${id}`,
      {
        method: "DELETE",
        headers: {
          "content-type": "application/json",
          browserSessionId,
        },
        body: JSON.stringify(
          driverActivityCommonPropsT.encode({
            trailerId: truckId,
            // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
            position: toPosition(location),
            time: DateTime.now(),
          })
        ),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete note");
    }

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Volume endpoints
export const addVolume = createErrorReportingAsyncThunk(
  `${prefix}/add-volume`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      cubicMeters: Decimal;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, cubicMeters, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/volume`,
      body: addEditVolumeActivityT.encode({
        cubicMeters,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const editVolume = createErrorReportingAsyncThunk(
  `${prefix}/edit-volume`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      cubicMeters: Decimal;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, cubicMeters, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).put({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/volume/${id}`,
      body: addEditVolumeActivityT.encode({
        cubicMeters,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const deleteVolume = createErrorReportingAsyncThunk(
  `${prefix}/delete-volume`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    const response = await apiFetch(
      `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/volume/${id}`,
      {
        method: "DELETE",
        headers: {
          "content-type": "application/json",
          browserSessionId,
        },
        body: JSON.stringify(
          driverActivityCommonPropsT.encode({
            trailerId: truckId,
            // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
            position: toPosition(location),
            time: DateTime.now(),
          })
        ),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete volume");
    }

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Weight endpoints
export const addWeight = createErrorReportingAsyncThunk(
  `${prefix}/add-weight`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      weightInKg: Decimal;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, weightInKg, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/weight`,
      body: addEditWeightActivityT.encode({
        weightInKg,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const editWeight = createErrorReportingAsyncThunk(
  `${prefix}/edit-weight`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      weightInKg: Decimal;
      id: string;
      trailerId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, weightInKg, id, trailerId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).put({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/weight/${id}`,
      body: addEditWeightActivityT.encode({
        weightInKg,
        trailerId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const deleteWeight = createErrorReportingAsyncThunk(
  `${prefix}/delete-weight`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    const response = await apiFetch(
      `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/weight/${id}`,
      {
        method: "DELETE",
        headers: {
          "content-type": "application/json",
          browserSessionId,
        },
        body: JSON.stringify(
          driverActivityCommonPropsT.encode({
            trailerId: truckId,
            // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
            position: toPosition(location),
            time: DateTime.now(),
          })
        ),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete weight");
    }

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Invoice reference endpoints
export const addInvoiceReference = createErrorReportingAsyncThunk(
  `${prefix}/add-invoice-reference`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      ref: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, ref, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/invoice-ref`,
      body: addEditInvoiceRefActivityT.encode({
        ref,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const editInvoiceReference = createErrorReportingAsyncThunk(
  `${prefix}/edit-invoice-reference`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      ref: string;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, ref, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).put({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/invoice-ref/${id}`,
      body: addEditInvoiceRefActivityT.encode({
        ref,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
        time: DateTime.now(),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

export const deleteInvoiceReference = createErrorReportingAsyncThunk(
  `${prefix}/delete-invoice-reference`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      id: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, id, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    const response = await apiFetch(
      `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/invoice-ref/${id}`,
      {
        method: "DELETE",
        headers: {
          "content-type": "application/json",
          browserSessionId,
        },
        body: JSON.stringify(
          driverActivityCommonPropsT.encode({
            trailerId: truckId,
            // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
            position: toPosition(location),
            time: DateTime.now(),
          })
        ),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete invoice reference");
    }

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(input.routeId)).unwrap();
  }
);

// Trip start endpoint
export const startTrip = createAsyncThunk(
  `${prefix}/start-trip`,
  async (
    input: {
      routeId: string;
      odometer: number;
      dateTime: DateTime | null;
      note: string;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, odometer, note, dateTime, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/trip-start`,
      body: startTripActivityT.encode({
        odometer,
        note: note || null,
        time: dateTime,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    // TODO: endpoint to only fetch activities, no need for fetch all?
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Trip end endpoint
export const endTrip = createAsyncThunk(
  `${prefix}/end-trip`,
  async (
    input: {
      routeId: string;
      odometer: number;
      dateTime: DateTime | null;
      note: string;
      trailerId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, odometer, note, dateTime, trailerId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/trip-end`,
      body: endTripActivityT.encode({
        odometer,
        note: note || null,
        time: dateTime,
        trailerId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    // TODO: endpoint to only fetch activities, no need for fetch all?
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Arrived endpoint
export const arrived = createAsyncThunk(
  `${prefix}/arrived`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      dateTime: DateTime | null;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, dateTime, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/arrived`,
      body: arrivedActivityT.encode({
        time: dateTime,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Departed endpoint
export const departed = createAsyncThunk(
  `${prefix}/departed`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      dateTime: DateTime | null;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, dateTime, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/departed`,
      body: departedActivityT.encode({
        time: dateTime,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);

// Stop Completed endpoint
export const stopCompleted = createAsyncThunk(
  `${prefix}/stop-completed`,
  async (
    input: {
      routeId: string;
      cargoId: string;
      stopId: string;
      dateTime: DateTime | null;
      truckId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const { routeId, cargoId, stopId, dateTime, truckId } = input;
    const location = await getPositionForDriverEventFromWatch();

    await api(thunkApi).post({
      url: `/drivers-api/routes/${routeId}/cargos/${cargoId}/stops/${stopId}/stop-completed`,
      body: stopCompletedActivityT.encode({
        time: dateTime,
        trailerId: truckId,
        // unpoweredTrailerId: input.unpoweredTrailerId, // TODO: re-enable trailer id on activities
        position: toPosition(location),
      }),
      decoder: noContentDecoder,
    });

    // Reload route data to get updated activities
    await thunkApi.dispatch(loadRouteWithStops(routeId)).unwrap();
  }
);
